/**
 * Wave L-1 (PLAN-CITY2 §6): `part` (a generic "weapon part") is the new
 * UNCOMMON/RARE-tier material `economy/drops.ts` grants — see that file's
 * own comment on why RARE ("weapons") folds into a bigger `part` payout
 * instead of a fabricated weapon-unlock system. Additive: every existing
 * `scrap` call site/consumer is unchanged.
 */
export type MaterialKind = 'scrap' | 'part';

export interface Wallet {
  coins: number;
  materials: Record<MaterialKind, number>;
}

export function createWallet(): Wallet {
  return { coins: 0, materials: { scrap: 0, part: 0 } };
}

/**
 * The single choke point for every coin-value change (Classic gotcha #25's
 * `spendCoins`/`grantCoins` pattern, carried into 3D) — no call site outside
 * this module may write `wallet.coins` directly. Clamped at 0: a kill-combo
 * or double-credit bug can only ever leave the wallet at zero, never a
 * visible negative balance.
 */
export function grantCoins(wallet: Wallet, amount: number): void {
  if (amount <= 0) return;
  wallet.coins += amount;
}

/** Returns false (and spends nothing) if the wallet can't afford it — callers must check the result before spending the effect the coins were paying for. */
export function spendCoins(wallet: Wallet, amount: number): boolean {
  if (amount <= 0 || wallet.coins < amount) return false;
  wallet.coins -= amount;
  return true;
}

export function grantMaterial(wallet: Wallet, kind: MaterialKind, amount: number): void {
  if (amount <= 0) return;
  wallet.materials[kind] += amount;
}

/** S11: the materials-side counterpart to `spendCoins` (mirrors it exactly) — the crafting bench's own choke point for spending scrap, so no call site outside this module writes `wallet.materials` directly either. */
export function spendMaterial(wallet: Wallet, kind: MaterialKind, amount: number): boolean {
  if (amount <= 0 || wallet.materials[kind] < amount) return false;
  wallet.materials[kind] -= amount;
  return true;
}
